/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useCallback, useEffect, useState } from "react";
import axios, { AxiosError } from "axios";
// types
import {
  Candidate,
  CandidateBulkCreation,
  CandidateBulkCreationError,
  CandidateCreate,
  CandidateCreateError,
  CandidateResponse,
  ConversationHistory,
  Feedbacks,
  GetAllCandidatesQuery,
  ICandidate,
  iCandidates,
  InterviewCreate,
  InterviewCreateError,
  InterviewEvaluationReport,
} from "../../../store/types/candidate";

// state and types
import { useAppDispatch } from "../../../store/hooks";
import { ErrorWarning } from "../../../store/types/misc";
import { toast } from "sonner";
import {
  candidate,
  getInterviewEvaluationById,
  getCandidatesByJobId,
  updateCandidate,
  candidateBulk,
  candidates,
  updateCandidat,
  candidatesApi,
} from "../../../services/api/candidate";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../../utility/axiosInstance";
import { DropdownOption } from "../../../components/elements/dropdown/types";

// get request new style
export const useCandidatess = (autoFetch = true) => {
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCandidates = async (params?: Record<string, any>) => {
    setLoading(true);
    setError(null);
    try {
      const response = await candidatesApi.getAll(params);
      setCandidates(response.data.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (autoFetch) {
      fetchCandidates();
    }
  }, [autoFetch]);

  return { candidates, loading, error, refetch: fetchCandidates };
};

export const useCandidateOptions = (autoFetch = true) => {
  const { candidates, loading, refetch } = useCandidatess(autoFetch);

  const candidateOptions: DropdownOption[] = candidates.map((candidate) => ({
    value: candidate?.id,
    label: `${candidate?.name} | ${candidate?.email}`,
  }));

  return { candidateOptions, loading, refetch };
};

// get candidates
export const useGetCandidatess = () => {
  const [candidates, setCandidates] = useState<iCandidates[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const getCandidates = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      const response =
        await axiosInstance.get<CandidateResponse>("/candidates");

      // Log the full response for debugging
      console.log("Full API Response:", response.data);

      // Extract candidates array from the nested data structure
      const candidatesData = response.data.data.data || [];

      // Validate the response data
      if (Array.isArray(candidatesData)) {
        setCandidates(candidatesData);
        console.log("Candidates fetched successfully:", candidatesData);
      } else {
        throw new Error("Invalid");
      }
    } catch (err) {
      const error =
        err instanceof Error
          ? err
          : new Error("An error occurred while fetching candidates");
      setError(error);
      console.error("Error fetching candidates:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    candidates,
    loading,
    error,
    getCandidates,
  };
};

// Create Candidate
export const useCandidat = (
  isEditMode: boolean = false,
  candidateId?: string,
  onSuccess?: () => void,
) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [emailExist, emailExistSet] = React.useState<ErrorWarning>("");
  const [data, dataSet] = React.useState<CandidateCreate>({
    name: "",
    email: "",
    phoneNumber: "",
    jobTitle: "",
    cv: null,
    address: "",
  });

  const [error, errorSet] = React.useState<CandidateCreateError>({
    name: "",
    email: "",
    phoneNumber: "",
    jobTitle: "",
  });

  // Expose setData function for external use (like populating form in edit mode)
  const setData = (newData: Partial<CandidateCreate>) => {
    dataSet((prev) => ({ ...prev, ...newData }));
    console.log(newData);
  };

  const onChangeHandlerBulk = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, files, value } = e.target;
    errorSet((prev) => ({ ...prev, [name]: "" }));
    if (files && files.length > 0) {
      console.log("File selected:", files[0]); // Debug: log selected file
      dataSet((prev) => ({ ...prev, [name]: files[0] })); // store File object
    } else {
      dataSet((prev) => ({ ...prev, [name]: value }));
    }
  };

  const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    errorSet((prev) => ({ ...prev, [name]: "" }));
    dataSet((prev) => ({ ...prev, [name]: value }));
    if (name === "email") {
      emailExistSet("");
    }
  };

  const onChangeHandle = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    errorSet((prev) => ({ ...prev, [name]: "" }));
    dataSet((prev) => ({ ...prev, [name]: value }));
  };

  const onValidateUser = (): boolean => {
    let error: boolean = true;
    if (!data.name) {
      errorSet((prev) => ({ ...prev, name: "warning" }));
      error = false;
    }
    if (!data.phoneNumber) {
      errorSet((prev) => ({ ...prev, phoneNumber: "warning" }));
      error = false;
    }
    // if (!data.email || !validateEmail(data.email) || emailExist) {
    //   errorSet((prev) => ({ ...prev, email: "warning" }));
    //   error = false;
    // }
    if (!data.email) {
      errorSet((prev) => ({ ...prev, email: "warning" }));
      error = false;
    }
    if (!data.jobTitle) {
      errorSet((prev) => ({ ...prev, interviewDate: "warning" }));
      error = false;
    }
    return error;
  };

  // Create candidate function
  const onCreate = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("name", data.name);
      formData.append("email", data.email);
      formData.append("phoneNumber", data.phoneNumber);
      formData.append(
        "jobTitle",
        data.jobTitle.toUpperCase()?.replace(/\s+/g, "_"),
      );
      formData.append("address", data.address || "");
      if (data.cv instanceof File) {
        formData.append("cv", data.cv);
      }

      await candidates(dispatch, formData);

      // clear state
      dataSet({
        name: "",
        email: "",
        phoneNumber: "",
        jobTitle: "",
        cv: null,
        address: "",
      });

      toast.success("Candidate created successfully");

      // Call the onSuccess callback if provided
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      const err = error as AxiosError<any>;
      const backendMessage =
        err?.response?.data?.error?.message || "Failed to create candidate";
      toast.error(backendMessage);
    } finally {
      setLoading(false);
    }
  };

  // Update candidate function (you'll need to implement the actual API call)
  const onUpdate = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("name", data.name);
      formData.append("email", data.email);
      formData.append("phoneNumber", data.phoneNumber);
      formData.append(
        "jobTitle",
        data.jobTitle.toUpperCase()?.replace(/\s+/g, "_"),
      );
      formData.append("address", data.address || "");
      if (data.cv instanceof File) {
        formData.append("cv", data.cv);
      }

      // Replace this with your actual update API call
      await updateCandidat(dispatch, candidateId!, formData);
      toast.success("Candidate updated successfully");

      // Call the onSuccess callback if provided
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      toast.error("Failed to update candidate");
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async () => {
    if (onValidateUser()) {
      if (isEditMode) {
        await onUpdate();
      } else {
        await onCreate();
      }
    }
  };

  return {
    data,
    setData,
    emailExist,
    error,
    onChangeHandler,
    onChangeHandle,
    onChangeHandlerBulk,
    onSubmit,
    isEditMode,
    loading,
  };
};

// Create Interviews
export const useCandidate = (
  isEditMode: boolean = false,
  candidateId?: string,
  onSuccess?: () => void,
) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(false);
  const [emailExist, emailExistSet] = React.useState<ErrorWarning>("");
  const [data, dataSet] = React.useState<InterviewCreate>({
    candidateIds: [""],
    // email: "",
    // phoneNumber: "",
    interviewDate: "",
    jobId: "",
    // interviewId: "",
    expirationDate: "",
    hiringSource: [],
  });

  const [error, errorSet] = React.useState<InterviewCreateError>({
    candidateIds: "",
    // email: "",
    // phoneNumber: "",
    interviewDate: "",
    jobId: "",
    expirationDate: "",
  });

  // Expose setData function for external use (like populating form in edit mode)
  const setData = (newData: Partial<InterviewCreate>) => {
    dataSet((prev) => ({ ...prev, ...newData }));
    console.log(newData);
  };

  const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    errorSet((prev) => ({ ...prev, [name]: "" }));
    dataSet((prev) => ({ ...prev, [name]: value }));
    if (name === "email") {
      emailExistSet("");
    }
  };

  const onChangeHandle = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    errorSet((prev) => ({ ...prev, [name]: "" }));
    dataSet((prev) => ({ ...prev, [name]: value }));
  };

  const onValidateUser = (): boolean => {
    let error: boolean = true;
    if (!data.candidateIds) {
      errorSet((prev) => ({ ...prev, name: "warning" }));
      error = false;
    }
    if (!data.expirationDate) {
      errorSet((prev) => ({ ...prev, expirationDate: "warning" }));
      error = false;
    }
    // if (!data.email || !validateEmail(data.email) || emailExist) {
    //   errorSet((prev) => ({ ...prev, email: "warning" }));
    //   error = false;
    // }
    // if (!data.email) {
    //   errorSet((prev) => ({ ...prev, email: "warning" }));
    //   error = false;
    // }
    if (!data.interviewDate) {
      errorSet((prev) => ({ ...prev, interviewDate: "warning" }));
      error = false;
    }
    return error;
  };

  // Create interview function
  const onCreate = async (overrideData?: Partial<InterviewCreate>) => {
    setLoading(true);
    const finalData = { ...data, ...overrideData };
    try {
      await candidate(dispatch, {
        ...finalData,
        interviewDate: finalData.interviewDate,
        expirationDate: finalData.expirationDate,
        candidateIds: finalData.candidateIds,
      });
      toast.success("Interview created successfully");
      navigate(`/interviews`);
    } catch (error) {
      // Error is already handled and displayed in the API call
      // Just need to prevent navigation
      const err = error as AxiosError<any>;
      const backendMessage =
        err?.response?.data?.error?.message ||
        err?.response?.data?.message ||
        "Failed to create interview";
      console.error("Interview creation failed:", backendMessage);
    } finally {
      setLoading(false);
    }
  };

  // Update/Reschedule interview function (you'll need to implement the actual API call)
  const onUpdate = async () => {
    setLoading(true);
    try {
      // Only send the required fields for rescheduling
      await updateCandidate(dispatch, candidateId!, {
        interviewDate: data.interviewDate,
        expirationDate: data.expirationDate,
      } as any);
      toast.success("Interview rescheduled successfully");

      // Call the onSuccess callback if provided to refetch data and close modal
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      // Error is already handled and displayed in the API call
      console.error(
        "Interview reschedule failed:",
        error?.response?.data?.error?.message || error?.response?.data?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (overrideData?: Partial<InterviewCreate>) => {
    if (onValidateUser()) {
      if (isEditMode) {
        await onUpdate();
      } else {
        await onCreate(overrideData);
      }
    }
  };

  return {
    data,
    setData, // Now exposed for external use
    emailExist,
    error,
    onChangeHandler,
    onChangeHandle,
    onSubmit,
    isEditMode,
    loading,
  };
};

// Bulk candidate create/upload
export const useBulkCandidates = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [data, dataSet] = React.useState<CandidateBulkCreation>({
    csvFile: null,
    jobId: "",
    interviewDate: "",
    expirationDate: "",
  });

  const [err, errSet] = React.useState<CandidateBulkCreationError>({
    csvFile: "",
    jobId: "",
    interviewDate: "",
    expirationDate: "",
  });

  const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, files, value } = e.target;
    errSet((prev) => ({ ...prev, [name]: "" }));
    if (name === "csvFile" && files && files.length > 0) {
      console.log("File selected:", files[0]); // Debug: log selected file
      dataSet((prev) => ({ ...prev, [name]: files[0] })); // store File object
    } else {
      dataSet((prev) => ({ ...prev, [name]: value }));
    }
  };

  const onChangeTextbox = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    dataSet((prev) => ({ ...prev, [name]: value }));
    errSet((prev) => ({ ...prev, [name]: "" }));
  };

  const onChangeHandle = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    errSet((prev) => ({ ...prev, [name]: "" }));
    dataSet((prev) => ({ ...prev, [name]: value }));
  };

  const onValidateUsers = (): boolean => {
    let error: boolean = true;
    if (!data.csvFile) {
      errSet((prev) => ({ ...prev, csvFile: "warning" }));
      error = false;
    }
    if (!data.jobId) {
      errSet((prev) => ({ ...prev, jobId: "warning" }));
      error = false;
    }
    if (!data.interviewDate) {
      errSet((prev) => ({ ...prev, interviewDate: "warning" }));
      error = false;
    }
    if (!data.expirationDate) {
      errSet((prev) => ({ ...prev, expirationDate: "warning" }));
      error = false;
    }
    return error;
  };

  // Create bulk candidate function
  const onCreateBulk = async () => {
    // Show loading toast with spinner
    const loadingToastId = toast.loading("Running bulk upload...", {
      description: "Please wait while we process your CSV file",
      duration: Infinity, // Keep it open until we dismiss it
    });

    try {
      const formData = new FormData();
      if (data.csvFile) {
        console.log("Appending file to FormData:", data.csvFile); // Debug: log file before append
        formData.append("file", data.csvFile);
      } else {
        console.warn("No file selected for upload.");
      }
      console.log("Appending jobId to FormData:", data.jobId); // Debug: log jobId
      formData.append("jobId", data.jobId);

      // Use dates as-is (they're already in correct format from DatePicker)
      // The dates are already formatted with toLocalISOString in the DatePicker onChange handlers
      const interviewDateISO = data.interviewDate || "";
      const expirationDateISO = data.expirationDate || "";

      formData.append("interviewDate", interviewDateISO);
      formData.append("expirationDate", expirationDateISO);
      console.log("Submitting FormData:", formData); // Debug: log FormData object

      const response = await candidateBulk(dispatch, formData);

      // Dismiss loading toast
      toast.dismiss(loadingToastId);

      // Extract data from response
      const responseData = response?.data || response;
      const message =
        responseData?.message ||
        "Bulk Candidates and interview created successfully";
      const summary = responseData?.data?.summary || responseData?.summary;

      // Build success message with formatted summary using JSX for better formatting
      if (summary && typeof summary === "object") {
        // Format summary as a readable list with bold labels - each on new line
        const summaryItems = [
          <div key="total" style={{ marginBottom: "4px" }}>
            <strong>✓ Total Processed:</strong> {summary.totalProcessed || 0}
          </div>,
          <div key="candidates" style={{ marginBottom: "4px" }}>
            <strong>✓ Candidates Created:</strong>{" "}
            {summary.candidatesCreated || 0}
          </div>,
          summary.candidatesExisted ? (
            <div key="existed" style={{ marginBottom: "4px" }}>
              <strong>⚠ Candidates Already Existed:</strong>{" "}
              {summary.candidatesExisted}
            </div>
          ) : null,
          <div key="interviews" style={{ marginBottom: "4px" }}>
            <strong>✓ Interviews Created:</strong>{" "}
            {summary.interviewsCreated || 0}
          </div>,
          summary.interviewsFailed ? (
            <div key="failed" style={{ marginBottom: "4px" }}>
              <strong>✗ Interviews Failed:</strong> {summary.interviewsFailed}
            </div>
          ) : null,
          <div key="emails" style={{ marginBottom: "4px" }}>
            <strong>✓ Emails Sent:</strong> {summary.emailsSent || 0}
          </div>,
          summary.emailsFailed ? (
            <div key="emailsFailed" style={{ marginBottom: "4px" }}>
              <strong>✗ Emails Failed:</strong> {summary.emailsFailed}
            </div>
          ) : null,
        ].filter(Boolean);

        // Show success toast with JSX description for proper formatting
        toast.success("Upload Complete!", {
          description: (
            <div style={{ lineHeight: "1.6" }}>
              <div style={{ marginBottom: "12px", fontWeight: 500 }}>
                {message}
              </div>
              <div style={{ marginTop: "12px" }}>{summaryItems}</div>
            </div>
          ),
          duration: 15000, // 15 seconds to give user time to read the summary
        });
      } else {
        // Show success toast with plain message
        toast.success("Upload Complete!", {
          description: message,
          duration: 15000,
        });
      }

      navigate(`/jobs`);
    } catch (error: any) {
      // Dismiss loading toast
      toast.dismiss(loadingToastId);

      // Extract error message from API response structure
      // Backend returns: { status: "error", message: "...", error: { name: "...", message: "..." } }
      let errorMessage = "Failed to create bulk candidates";

      if (error?.response?.data) {
        const errorData = error.response.data;
        // Check for error.message (nested error object)
        if (errorData?.error?.message) {
          errorMessage = errorData.error.message;
        }
        // Check for error.message (direct message)
        else if (errorData?.message) {
          errorMessage = errorData.message;
        }
        // Check for error (direct error string/object)
        else if (errorData?.error) {
          errorMessage =
            typeof errorData.error === "string"
              ? errorData.error
              : errorData.error.message || errorData.error.toString();
        }
      }
      // Fallback to error message if available
      else if (error?.message) {
        errorMessage = error.message;
      }

      // Show error toast - don't throw to prevent uncaught runtime errors
      toast.error("Upload Failed", {
        description: errorMessage,
        duration: 5000,
      });

      // Don't re-throw the error to prevent uncaught runtime errors
      // The error is already handled and displayed to the user
      console.error("Bulk upload error:", error);
    }
  };

  const onSubmit = async () => {
    if (onValidateUsers()) {
      {
        await onCreateBulk();
      }
    }
  };

  return {
    data,
    err,
    onChangeHandler,
    onChangeTextbox,
    onChangeHandle,
    onSubmit,
  };
};

export const useCandidates = ({
  jobId,
  recommendationStatus,
  page = "1",
  limit = "10",
}: {
  jobId?: string;
  recommendationStatus?: string;
  page?: string;
  limit?: string;
}) => {
  const [candidates, setCandidates] = useState<ICandidate[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchCandidates = useCallback(async () => {
    if (!jobId) return;

    try {
      setLoading(true);

      const response = await getCandidatesByJobId(jobId, recommendationStatus);

      setCandidates(response.data);
    } finally {
      setLoading(false);
    }
  }, [jobId, recommendationStatus]);

  // fetch whenever jobId or status changes
  useEffect(() => {
    fetchCandidates();
  }, [fetchCandidates]);

  return {
    candidates,
    loading,
    refetch: fetchCandidates,
  };
};

export const ViewInterview = (interviewId: string, id: string) => {
  const [candidateEvaluation, setCandidateEvaluation] =
    useState<InterviewEvaluationReport>();
  const [conversationHistory, setConversationHistory] = useState<
    ConversationHistory[] | null
  >(null);
  const [candidateVideoUrl, setCandidateVideoUrl] = useState<string>("");
  const [candidateFrontendVideoUrl, setCandidateFrontendVideoUrl] = useState<string>("");
  const [candidateAudioUrl, setCandidateAudioUrl] = useState<string>("");
  const [candidateFeedback, setCandidateFeedback] = useState<Feedbacks | null>(
    null,
  );
  const [candidateScreenshots, setCandidateScreenshots] = useState<string>("");
  const [candidateEmail, setCandidateEmail] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchInterviewEvaluation = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getInterviewEvaluationById(interviewId, id);
      setCandidateEvaluation(response.data.candidateEvaluation);
      setConversationHistory(response.data.conversationHistory);
      setCandidateVideoUrl(response.data?.videoDetails?.videoUrl ?? "");
      setCandidateFrontendVideoUrl(response.data?.videoDetails?.frontendVideoUrl ?? "");
      setCandidateAudioUrl(response.data?.audioDetails?.audioUrl ?? "");
      setCandidateFeedback(response.data?.feedback || null);
      setCandidateScreenshots(response.data?.screenshotUrl || "");
      setCandidateEmail(response.data?.candidateEmail || "");
    } catch (err) {
      const errorMessage = "No conversation found for this interview";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchInterviewEvaluation();
    }
  }, [id]);

  return {
    candidateEvaluation,
    conversationHistory,
    candidateVideoUrl,
    candidateFrontendVideoUrl,
    candidateAudioUrl,
    candidateFeedback,
    candidateScreenshots,
    candidateEmail,
    loading,
    error,
    refetch: fetchInterviewEvaluation,
  };
};
